import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TabAvisGoogle from '../tabs/TabAvisGoogle';
import ReplyModal from '../components/ReplyModal/ReplyModal';
import { apiGet, apiPost, apiPut } from '../utils/api-client';
import { normalizeTimestampISO } from '../utils/normalizeTimestamp';
import { useToast } from '../components/Toast';

/**
 * TabAvisGoogleContainer — Sprint 1 + Phase 2
 *
 * Endpoints branchés :
 *  - GET  /api/dashboard/google-reviews?sort=&page=&limit=
 *  - POST /api/dashboard/sync-google
 *  - GET  /api/gmb/status                              (pour la modal de réponse)
 *  - POST /api/dashboard/review-replies/generate       (génération IA)
 *  - PUT  /api/dashboard/review-replies/:id            (publication / brouillon)
 */
export default function TabAvisGoogleContainer() {
  const { t } = useTranslation('tabs');
  const [reviews, setReviews]         = useState([]);
  const [total, setTotal]             = useState(0);
  const [source, setSource]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [syncing, setSyncing]         = useState(false);
  const [error, setError]             = useState(null);
  const [hasMore, setHasMore]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sort, setSort]               = useState('newest');
  const [page, setPage]               = useState(1);
  const [restaurant, setRestaurant]   = useState(null);

  // Modal de réponse IA
  const [replyTarget, setReplyTarget] = useState(null);
  const [gmbConnected, setGmbConnected] = useState(false);

  const toast = useToast();

  const PAGE_SIZE = 20;

  /**
   * Normalise un avis backend pour le frontend.
   * Le backend retourne author_name / text / review_time / rating.
   * Le frontend (TabAvisGoogle, ReplyModal) attend author / text / id / date.
   */
  const normalizeReview = (r) => {
    // Génère un ID stable basé sur review_time + auteur (le backend n'envoie pas d'id)
    // review_time peut être ISO string / Unix s / Unix ms / bigint pg → on normalise
    const normalizedDate = normalizeTimestampISO(r.review_time);
    const stableId = normalizedDate
      ? `gr-${normalizedDate}-${(r.author_name || 'anon').slice(0, 8)}`
      : `gr-${(r.author_name || 'anon')}-${(r.text || '').slice(0, 20).replace(/\s/g, '')}`;
    return {
      ...r,
      id: r.id || r.review_id || stableId,
      author: r.author || r.author_name || 'Client anonyme',
      text: r.text || r.review_text || '',
      rating: r.rating || 0,
      // Normalise review_time hétérogène (ISO string / Unix s / Unix ms) → ISO string ou null
      date: r.date || normalizeTimestampISO(r.review_time),
      replied: !!r.replied || !!r.reply_text,
    };
  };

  const loadReviews = useCallback(async (sortValue, pageNum = 1) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const data = await apiGet(
        `/api/dashboard/google-reviews?sort=${sortValue}&page=${pageNum}&limit=${PAGE_SIZE}`
      );
      const list = data?.reviews || data?.data || data || [];
      const pageReviews = (Array.isArray(list) ? list : []).map(normalizeReview);

      if (pageNum === 1) {
        setReviews(pageReviews);
      } else {
        setReviews((prev) => [...prev, ...pageReviews]);
      }
      setTotal(data?.total ?? pageReviews.length);
      setSource(data?.source || (data?.live ? 'live' : 'cache'));
      setHasMore(pageReviews.length >= PAGE_SIZE);
      setPage(pageNum);
    } catch (e) {
      setError(e.message || t('avis_google.error_text_default'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [t]);

  // Au mount : charger les avis + le restaurant + statut GMB
  useEffect(() => {
    loadReviews(sort, 1);
    (async () => {
      try {
        const [dashData, gmbData] = await Promise.all([
          apiGet('/api/dashboard/').catch(() => null),
          apiGet('/api/gmb/status').catch(() => ({ connected: false })),
        ]);
        if (dashData?.restaurant) setRestaurant(dashData.restaurant);
        setGmbConnected(!!gmbData?.connected);
      } catch (e) {
        console.error('[TabAvisGoogle] init error:', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    try {
      await apiPost('/api/dashboard/sync-google');
      await loadReviews(sort, 1);
    } catch (e) {
      setError(e.message || t('avis_google.alerts.sync_failed'));
    } finally {
      setSyncing(false);
    }
  }, [loadReviews, sort, t]);

  const handleSortChange = useCallback((newSort) => {
    setSort(newSort);
    loadReviews(newSort, 1);
  }, [loadReviews]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) loadReviews(sort, page + 1);
  }, [loadReviews, sort, page, loadingMore, hasMore]);

  const handleRefresh = useCallback(() => loadReviews(sort, 1), [loadReviews, sort]);

  /** Ouvre la modal de réponse IA pour un avis. */
  const handleReply = useCallback((review) => {
    setReplyTarget(review);
  }, []);

  /** Génère une réponse IA via Claude Haiku. */
  const handleGenerate = useCallback(async (review) => {
    // Si pas de texte, on ne peut pas générer (le backend exige review_text)
    if (!review.text || review.text.trim().length === 0) {
      alert(t('avis_google.alerts.ia_no_text'));
      return '';
    }
    try {
      const data = await apiPost('/api/dashboard/review-replies/generate', {
        review_id:     review.id || review.review_id,
        author_name:   review.author || review.author_name || 'un client',
        review_text:   review.text,
        review_rating: review.rating || 0,
      });
      return data?.reply_text || data?.text || data?.draft || '';
    } catch (e) {
      alert(t('avis_google.alerts.ia_generate_failed', { error: e.message }));
      return '';
    }
  }, [t]);

  /** Publie la réponse sur Google. */
  const handlePublish = useCallback(async (review, text) => {
    try {
      const replyId = review.replyId || review.reply_id || review.id;
      await apiPut(`/api/dashboard/review-replies/${replyId}`, {
        reply_text: text,
        action: 'publish',
      });
      await loadReviews(sort, 1); // refetch pour voir le statut "publié"
      return true;
    } catch (e) {
      alert(t('avis_google.alerts.publish_failed', { error: e.message }));
      return false;
    }
  }, [t, loadReviews, sort]);

  /** Ouvrir l'avis sur Google Maps — fallback intelligent si URL directe absente. */
  const handleViewOnGoogle = useCallback((review) => {
    // 1. URL directe sur l'avis (si Google Business connecté)
    if (review?.googleUrl) {
      window.open(review.googleUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    // 2. URL de la fiche Google Maps du restaurant
    if (restaurant?.google_maps_url) {
      window.open(restaurant.google_maps_url, '_blank', 'noopener,noreferrer');
      return;
    }
    // 3. Fallback : recherche Google Maps avec le place_id
    if (restaurant?.google_place_id) {
      window.open(
        `https://www.google.com/maps/place/?q=place_id:${restaurant.google_place_id}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    // 4. Dernier fallback : recherche Google par nom
    if (restaurant?.nom) {
      window.open(
        `https://www.google.com/search?q=${encodeURIComponent(restaurant.nom + ' ' + (restaurant.ville || ''))}`,
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    toast.warning(t('avis_google.alerts.google_link_unavailable'));
  }, [t, restaurant, toast]);

  return (
    <>
      <TabAvisGoogle
        reviews={reviews}
        total={total}
        googleTotal={restaurant?.avis_depart ?? null}
        source={source}
        loading={loading}
        syncing={syncing}
        error={error}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onSync={handleSync}
        onRefresh={handleRefresh}
        onSortChange={handleSortChange}
        onLoadMore={handleLoadMore}
        onRetry={handleRefresh}
        onReply={handleReply}
        onViewOnGoogle={handleViewOnGoogle}
      />
      {replyTarget && (
        <ReplyModal
          review={replyTarget}
          gmbConnected={gmbConnected}
          onClose={() => setReplyTarget(null)}
          onGenerate={handleGenerate}
          onPublish={handlePublish}
        />
      )}
    </>
  );
}
